import { action } from "typesafe-actions";
import { upsertItemInListById } from "./helpers";

/** Single generic action type for upserting an item in any slice's list (no API, local-only). */
export const UPSERT_ITEM_IN_LIST = "UPSERT_ITEM_IN_LIST";

export type UpsertItemInListPayload = {
  slice: string;
  item: Record<string, unknown>;
};

export const upsertItemInList = (slice: string, item: Record<string, unknown>) =>
  action(UPSERT_ITEM_IN_LIST, { slice, item } as UpsertItemInListPayload);

/** Config per slice: where the list lives and which key is the id. */
export type UpsertListConfig = { listPath: string; idKey: string };

/**
 * Register a slice for the generic upsert-in-list behavior.
 * Add one entry per slice that supports local list updates (e.g. dialog/right panel without API).
 */
export const UPSERT_LIST_CONFIG: Record<string, UpsertListConfig> = {};

export function registerUpsertListConfig(
  slice: string,
  listPath: string,
  idKey: string
): void {
  UPSERT_LIST_CONFIG[slice] = { listPath, idKey };
}

function getIn(obj: unknown, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = obj;
  for (const p of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[p];
  }
  return current;
}

function setInImmutable(obj: unknown, path: string, value: unknown): unknown {
  const parts = path.split(".");
  if (parts.length === 0) return value;
  const [head, ...rest] = parts;
  if (rest.length === 0) {
    return typeof obj === "object" && obj !== null && !Array.isArray(obj)
      ? { ...(obj as Record<string, unknown>), [head]: value }
      : value;
  }
  const next = getIn(obj, head) ?? {};
  const updated = setInImmutable(next, rest.join("."), value);
  return typeof obj === "object" && obj !== null && !Array.isArray(obj)
    ? { ...(obj as Record<string, unknown>), [head]: updated }
    : obj;
}

/**
 * Applies upsert to a slice state: updates the list at listPath by idKey.
 * Used by root reducer only.
 */
export function applyUpsertToSlice(
  sliceState: unknown,
  listPath: string,
  idKey: string,
  item: Record<string, unknown>
): unknown {
  const list = getIn(sliceState, listPath);
  const arr = Array.isArray(list) ? list : [];
  const idField = idKey as keyof typeof item;
  const newList = upsertItemInListById(arr, item, idField);
  return setInImmutable(sliceState, listPath, newList);
}

// --- Registrations: add one line per slice that uses upsert-in-list ---
registerUpsertListConfig("crossroadsLeads", "list.list", "crossroads_lead_uuid");
registerUpsertListConfig("leads", "list.list", "leads_uuid");
